/**
 * Pure geometry for the band-scale drawing on the grading strategy editor.
 *
 * Turns a strategy's numeric shape — score range, bands, threshold — into
 * unit-interval coordinates the SVG maps onto its width. Kept apart from the
 * component so the interesting decisions (what counts as a gap, where a seam
 * is drawn, what happens to a band that leaks outside the range) are testable
 * without a DOM.
 *
 * ADJACENCY MATCHES THE VALIDATOR. Both band ends are inclusive and two
 * conventions are legal — shared boundaries (0–10, 10–20) and stepped ones
 * (0–9.99, 10–20) — so "gap" here is the validator's gap: more than one
 * smallest representable step (10^-decimalPlaces) between consecutive bands,
 * measured in integer step units to keep float noise out. A legal one-step
 * seam is NOT drawn as a gap; the earlier band is visually extended to the
 * next band's start, because a 0.01-wide slit of background would read as an
 * error the validator does not report.
 *
 * Gaps and overlaps become their own segments so the component can render
 * them in a hatched error pattern: an invalid configuration must be VISIBLE
 * before the server says so.
 */

export interface BandInput {
  code: string;
  label: string;
  minInclusive: number;
  maxInclusive: number;
  gradePoints?: number | null;
}

export interface ScaleSegment {
  kind: 'band' | 'gap' | 'overlap';
  /** Raw score extent, as authored. */
  from: number;
  to: number;
  /** Unit-interval extent, after seam-extension and clamping to the range. */
  x0: number;
  x1: number;
  /** Band segments only. */
  code?: string;
  label?: string;
  gradePoints?: number | null;
  /** Stable per sorted band position — drives the colour cycle. */
  colourIndex?: number;
  /** Gap/overlap segments: the neighbouring band codes, low side first. */
  between?: [string, string];
}

export interface ScaleLayout {
  /** True when minScore >= maxScore: there is no axis to draw on. */
  degenerate: boolean;
  segments: ScaleSegment[];
  threshold: { value: number; x: number; inRange: boolean };
  /** Band boundaries plus the range ends — the values worth labelling. */
  ticks: { value: number; x: number }[];
}

export interface ScaleLayoutInput {
  minScore: number;
  maxScore: number;
  passThreshold: number;
  decimalPlaces: number;
  bands: BandInput[];
}

export function computeScaleLayout(input: ScaleLayoutInput): ScaleLayout {
  const { minScore, maxScore, passThreshold } = input;
  const span = maxScore - minScore;
  if (!(span > 0) || !Number.isFinite(span)) {
    return {
      degenerate: true,
      segments: [],
      threshold: { value: passThreshold, x: 0, inRange: false },
      ticks: [],
    };
  }

  const decimalPlaces = Number.isInteger(input.decimalPlaces) ? input.decimalPlaces : 0;
  const step = Math.pow(10, -decimalPlaces);
  const inSteps = (value: number): number => Math.round(value / step);
  const clamp = (value: number): number => Math.min(1, Math.max(0, value));
  const toX = (value: number): number => clamp((value - minScore) / span);

  const bands = [...input.bands].sort(
    (a, b) => a.minInclusive - b.minInclusive || a.maxInclusive - b.maxInclusive,
  );

  const segments: ScaleSegment[] = [];

  // Leading gap: the validator's "lowest band starts late", drawn from the
  // range floor to the first band. An edge band code is synthesised so the
  // tooltip can still say what the gap sits between.
  if (bands.length > 0 && inSteps(bands[0].minInclusive) > inSteps(minScore)) {
    segments.push({
      kind: 'gap',
      from: minScore,
      to: bands[0].minInclusive,
      x0: 0,
      x1: toX(bands[0].minInclusive),
      between: ['—', bands[0].code],
    });
  }

  for (let i = 0; i < bands.length; i += 1) {
    const band = bands[i];
    const next = i + 1 < bands.length ? bands[i + 1] : null;
    const separation = next ? inSteps(next.minInclusive) - inSteps(band.maxInclusive) : null;

    // Seam-extension: a legal adjacency (shared boundary or one step) draws
    // the band flush to its neighbour so no phantom slit appears. The last
    // band is likewise extended to the range ceiling when it legally ends
    // there — an inclusive 19.99 end on a 2-decimal scale IS the top.
    let drawnEnd = band.maxInclusive;
    if (next && separation !== null && separation >= 0 && separation <= 1) {
      drawnEnd = next.minInclusive;
    } else if (!next && inSteps(band.maxInclusive) === inSteps(maxScore)) {
      drawnEnd = maxScore;
    }

    segments.push({
      kind: 'band',
      from: band.minInclusive,
      to: band.maxInclusive,
      x0: toX(band.minInclusive),
      x1: toX(Math.max(drawnEnd, band.minInclusive)),
      code: band.code,
      label: band.label,
      gradePoints: band.gradePoints ?? null,
      colourIndex: i,
    });

    if (next && separation !== null) {
      if (separation < 0) {
        // Interior intersection — the validator's overlap. Drawn over the top
        // of both bands so the contested scores are the hatched ones.
        segments.push({
          kind: 'overlap',
          from: next.minInclusive,
          to: band.maxInclusive,
          x0: toX(next.minInclusive),
          x1: toX(band.maxInclusive),
          between: [band.code, next.code],
        });
      } else if (separation > 1) {
        segments.push({
          kind: 'gap',
          from: band.maxInclusive,
          to: next.minInclusive,
          x0: toX(band.maxInclusive),
          x1: toX(next.minInclusive),
          between: [band.code, next.code],
        });
      }
    }
  }

  // Trailing gap: the validator's "highest band ends early".
  if (bands.length > 0) {
    const lastBand = bands[bands.length - 1];
    if (inSteps(lastBand.maxInclusive) < inSteps(maxScore)) {
      segments.push({
        kind: 'gap',
        from: lastBand.maxInclusive,
        to: maxScore,
        x0: toX(lastBand.maxInclusive),
        x1: 1,
        between: [lastBand.code, '—'],
      });
    }
  }

  // Ticks: the range ends and every band start — the numbers an operator
  // checks against the ministerial table. Deduplicated in step units so a
  // shared boundary does not print twice.
  const tickValues = new Map<number, number>();
  tickValues.set(inSteps(minScore), minScore);
  tickValues.set(inSteps(maxScore), maxScore);
  for (const band of bands) {
    tickValues.set(inSteps(band.minInclusive), band.minInclusive);
  }
  const ticks = [...tickValues.values()]
    .filter((value) => value >= minScore && value <= maxScore)
    .sort((a, b) => a - b)
    .map((value) => ({ value, x: toX(value) }));

  return {
    degenerate: false,
    segments,
    threshold: {
      value: passThreshold,
      x: toX(passThreshold),
      inRange: passThreshold >= minScore && passThreshold <= maxScore,
    },
    ticks,
  };
}
